import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Stack, Typography } from '@mui/material';
import { useSimpleTreeViewApiRef } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import {
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useGetStorageQuery,
  useRenameFolderMutation,
  useUploadStorageFilesMutation,
} from '~/api/v2/hooks';
import { StorageContext, useFilesToDisplayState, useStorageFileUploader } from '~/hooks';
import { ArrowDownIcon, ArrowNextIcon } from '~/icons';
import { findFolderByPath } from '~/utils';
import { FilesListBlock, RootFolderTreeItem, StorageStatsBlock } from '..';
import { buildFolderTreeNodes, setItemSelection } from '../utils';
import {
  folderTreeBlockStyles,
  folderTreeContainerStyles,
  folderTreeHeaderStyles,
  folderTreeStackStyles,
  folderTreeStyles,
} from './styles';

const FolderTree = () => {
  const apiRef = useSimpleTreeViewApiRef();
  const { data, isFetching } = useGetStorageQuery({});
  const mutations = {
    upload: useUploadStorageFilesMutation({ fixedCacheKey: 'storage-upload-files' })[1],
    create: useCreateFolderMutation({ fixedCacheKey: 'create-folder' })[1],
    rename: useRenameFolderMutation({ fixedCacheKey: 'rename-folder' })[1],
    delete: useDeleteFolderMutation({ fixedCacheKey: 'delete-folder' })[1],
  };
  const { handleUploadedFiles, isFilesUploading, uploadingFiles } = useStorageFileUploader();
  const [filesToDisplay, setFilesToDisplay] = useFilesToDisplayState(null);
  const [lastClickedItem, setLastClickedItem] = useState<string | null>('');
  const [expandedItems, setExpandedItems] = useState<string[]>(['']);
  const [previousClickedItem, setPreviousClickedItem] = useState<string | null>(null);

  const treeNodes = useMemo(() => data?.map(buildFolderTreeNodes), [data]);

  const addToExpanded = useCallback((itemId: string) => {
    setExpandedItems((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
  }, []);

  const handleItemClick = useCallback(
    (_: SyntheticEvent, itemId: string) => {
      if (itemId === lastClickedItem) return;

      setItemSelection(apiRef, itemId, true);
      setLastClickedItem(itemId);
      addToExpanded(itemId);
    },
    [lastClickedItem, data, addToExpanded, setItemSelection, apiRef],
  );

  // sync filesToDisplay with lastClickedItem changes
  useEffect(() => {
    //@ts-ignore
    if (!lastClickedItem) return setFilesToDisplay(data);
    const folder = findFolderByPath(data || [], lastClickedItem);
    if (folder) setFilesToDisplay(folder);
  }, [lastClickedItem, data, apiRef.current]);

  // declare previousClickedItem on rename/delete start
  useEffect(() => {
    if ((mutations.rename.isLoading || mutations.delete.isLoading) && lastClickedItem) {
      setPreviousClickedItem(lastClickedItem);
    }
  }, [mutations.rename.isLoading, mutations.delete.isLoading, lastClickedItem]);

  // after delete/rename keep expanded parent folder
  useEffect(() => {
    const shouldNavigateToParent = mutations.rename.isSuccess || mutations.delete.isSuccess;
    if (!shouldNavigateToParent || !previousClickedItem) return;

    const parentPath = previousClickedItem.split('/').slice(0, -1).join('/');
    const parentFolder = findFolderByPath(data || [], parentPath);

    if (parentFolder) {
      setLastClickedItem(parentPath);
      setFilesToDisplay(parentFolder);
      addToExpanded(parentPath);
      setPreviousClickedItem(null);
    } else {
      setLastClickedItem('');
    }
  }, [mutations.rename.isSuccess, mutations.delete.isSuccess, previousClickedItem, addToExpanded]);

  const treeViewSlots = useMemo(
    () => ({
      expandIcon: ({ props }: { props: Record<string, unknown> }) => <ArrowNextIcon {...props} fill="#0000008C" />,
      collapseIcon: ({ props }: { props: Record<string, unknown> }) => <ArrowDownIcon {...props} fill="#0000008C" />,
    }),
    [],
  );

  if (isFetching) {
    return (
      <StorageContext.Provider value={{ lastClickedItem, handleUploadedFiles, isFilesUploading, uploadingFiles }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <LoadingSpinner spinnerSize="m" />
        </Box>
      </StorageContext.Provider>
    );
  }

  return (
    <StorageContext.Provider value={{ lastClickedItem, handleUploadedFiles, isFilesUploading, uploadingFiles }}>
      <Stack sx={folderTreeStackStyles} spacing={2}>
        <StorageStatsBlock />
        <Box sx={folderTreeBlockStyles}>
          <Box sx={folderTreeContainerStyles}>
            <Typography sx={folderTreeHeaderStyles} variant="h6">
              Folders
            </Typography>
            <SimpleTreeView
              apiRef={apiRef}
              sx={folderTreeStyles}
              selectedItems={lastClickedItem}
              expandedItems={expandedItems}
              onExpandedItemsChange={(_, itemIds) => setExpandedItems(itemIds)}
              slots={treeViewSlots}
              onItemClick={handleItemClick}
            >
              <RootFolderTreeItem>{treeNodes}</RootFolderTreeItem>
            </SimpleTreeView>
          </Box>
          <FilesListBlock files={filesToDisplay} />
        </Box>
      </Stack>
    </StorageContext.Provider>
  );
};

export default FolderTree;
